import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { T } from "@/components/translation/T";
import { saveGuestOrderToken } from "@/lib/guestOrderAccess";
import { formatCents, parseOrderQuote, type OrderQuote } from "@/lib/checkoutQuote";
import { 
  CreditCard, 
  Truck, 
  Shield, 
  ArrowLeft,
  Loader2 
} from "lucide-react";

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
}

interface RealCheckoutProps {
  cartItems: CartItem[];
  onBack: () => void;
  onSuccess: () => void;
  /** Display-only name (Stage 3). Checkout data always uses the original item.name. */
  displayName?: (id: string, name: string) => string;
  /** Expected store; the server rejects carts containing products from another store. */
  storeId?: string;
}

interface CheckoutForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  notes: string;
}

export const RealCheckout = ({ cartItems, onBack, onSuccess, displayName, storeId }: RealCheckoutProps) => {
  const [formData, setFormData] = useState<CheckoutForm>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    notes: ''
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal'>('stripe');
  const { toast } = useToast();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setIsSignedIn(!!data.session));
  }, []);

  // Summary amounts come ONLY from the server (same cents and currency as the Stripe session).
  const [quote, setQuote] = useState<OrderQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [amountNotice, setAmountNotice] = useState<string | null>(null);
  const cartKey = cartItems.map((i) => `${i.id}:${i.quantity}`).join(",");

  const readError = async (error: unknown): Promise<{ code?: string; error?: string; details?: any } | null> => {
    try { return await (error as any)?.context?.json?.(); } catch { return null; }
  };

  useEffect(() => {
    let cancelled = false;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      const country = formData.country.trim() || "-";
      const addr = { fullName: "-", address: "-", city: "-", postalCode: "-", country };
      const { data, error } = await supabase.functions.invoke('create-order', {
        body: {
          quote_only: true,
          items: cartItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
          shippingAddress: addr,
          billingAddress: addr,
          ...(storeId ? { store_id: storeId } : {}),
        },
      });
      if (cancelled) return;
      const q = error ? null : parseOrderQuote(data?.amounts);
      if (q) {
        setQuote(q);
        setQuoteError(null);
      } else {
        const b = error ? await readError(error) : null;
        if (cancelled) return;
        setQuote(null);
        setQuoteError(b?.error ?? "Impossible de calculer le montant de la commande. Veuillez réessayer.");
      }
      setQuoteLoading(false);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey, formData.country, storeId]);

  const lineFor = (id: string) => quote?.lines.find((l) => l.product_id === id);
  const money = (cents: number) => (quote ? formatCents(cents, quote.currency) : "");

  const handleInputChange = (field: keyof CheckoutForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const requiredFields = ['email', 'firstName', 'lastName', 'address', 'city', 'postalCode'];
    
    for (const field of requiredFields) {
      if (!formData[field as keyof CheckoutForm]) {
        toast({
          title: "Champs requis manquants",
          description: `Veuillez remplir tous les champs obligatoires`,
          variant: "destructive"
        });
        return false;
      }
    }

    if (!formData.email.includes('@')) {
      toast({
        title: "Email invalide",
        description: "Veuillez entrer une adresse email valide",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const processStripePayment = async () => {
    // Signed-in buyer: unchanged. No account: guest checkout with the form's email.
    const { data: { user } } = await supabase.auth.getUser();
    const isGuest = !user;

    const errMsg = async (error: unknown, fallback: string) => {
      const b = await readError(error);
      return (b?.error as string) || fallback;
    };
    if (!quote) throw new Error(quoteError ?? "Le montant de la commande n'est pas encore calculé.");

    const address = {
      fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      address: formData.address,
      city: formData.city,
      postalCode: formData.postalCode,
      country: formData.country,
    };

    // 1) Order created server-side: prices, shipping, tax and stock computed from the database.
    const { data: order, error: orderError } = await supabase.functions.invoke('create-order', {
      body: {
        items: cartItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
        shippingAddress: address,
        billingAddress: address,
        ...(storeId ? { store_id: storeId } : {}),
        ...(isGuest ? { customer_email: formData.email.trim() } : {}),
        // The order is refused if the server total differs from the summary shown.
        expected_total_cents: quote.total_cents,
        expected_currency: quote.currency,
      },
    });
    if (orderError) {
      const b = await readError(orderError);
      const fresh = b?.code === "AMOUNT_CHANGED" ? parseOrderQuote(b?.details?.amounts) : null;
      if (fresh) {
        setQuote(fresh);
        setAmountNotice("Le montant de la commande a changé. Vérifiez le nouveau total avant de payer.");
        return;
      }
      throw new Error((b?.error as string) || "Impossible de créer la commande");
    }
    if (!order?.orderId) {
      throw new Error(await errMsg(orderError, "Impossible de créer la commande"));
    }
    const guestToken: string | undefined = isGuest ? order.guestToken : undefined;
    if (isGuest && !guestToken) throw new Error("Impossible de créer la commande");
    if (guestToken) saveGuestOrderToken(order.orderId, guestToken);

    // 2) Stripe Checkout Session on the merchant's connected account (amount read from the order).
    // Leaving Stripe without paying lands on the order page, which offers to resume the same order.
    const successPath = guestToken ? `/commande/${order.orderId}?t=${guestToken}` : `/commande/${order.orderId}`;
    const cancelPath = `${successPath}${guestToken ? "&" : "?"}paiement=interrompu`;
    const { data, error } = await supabase.functions.invoke('create-order-checkout', {
      body: {
        order_id: order.orderId,
        success_path: successPath,
        cancel_path: cancelPath,
        ...(guestToken ? { guest_token: guestToken } : {}),
      },
    });
    if (error || !data?.url) {
      throw new Error(await errMsg(error, "Le paiement par carte est momentanément indisponible."));
    }
    window.location.href = data.url;
  };

  const processPayPalPayment = async () => {
    // PayPal integration would go here
    toast({
      title: "PayPal",
      description: "Intégration PayPal bientôt disponible",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    setAmountNotice(null);
    
    try {
      setIsProcessing(true);
      
      if (paymentMethod === 'stripe') {
        await processStripePayment();
      } else {
        await processPayPalPayment();
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Erreur de paiement",
        description: error instanceof Error && error.message ? error.message : "Une erreur est survenue lors du traitement du paiement",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          <T>Retour au panier</T>
        </Button>
        <div>
          <h1 className="text-2xl font-bold"><T>Finaliser la commande</T></h1>
          <p className="text-muted-foreground">
            <T>Complétez vos informations pour finaliser votre achat</T>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Checkout Form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                <T>Informations de facturation</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName"><T>Prénom</T> *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email"><T>Email</T> *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                  {isSignedIn === false && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <T>Aucun compte requis : un lien de suivi de votre commande vous sera fourni après le paiement.</T>
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone"><T>Téléphone</T></Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                <T>Adresse de livraison</T>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address"><T>Adresse</T> *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city"><T>Ville</T> *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode"><T>Code postal</T> *</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country"><T>Pays</T> *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes"><T>Notes de commande</T></Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Instructions spéciales pour la livraison..."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle><T>Récapitulatif de commande</T></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium" translate="no">{displayName ? displayName(item.id, item.name) : item.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Quantité: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {lineFor(item.id) ? money(lineFor(item.id)!.line_cents) : "—"}
                      </p>
                    </div>
                  </div>
                ))}

                <hr />
                
                {quoteLoading && !quote ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <T>Calcul du montant…</T>
                  </div>
                ) : quote ? (
                  <div className="space-y-2" aria-busy={quoteLoading}>
                    <div className="flex justify-between">
                      <span><T>Sous-total</T></span>
                      <span>{money(quote.subtotal_cents)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>
                        <T>Livraison</T>
                        {quote.shipping_method && <span className="text-muted-foreground" translate="no"> ({quote.shipping_method.name})</span>}
                      </span>
                      <span>{quote.shipping_configured && quote.shipping_free ? <T>Offerte</T> : money(quote.shipping_cents)}</span>
                    </div>
                    {!quote.shipping_configured && (
                      <p className="text-xs text-muted-foreground"><T>Aucuns frais de livraison ne sont appliqués par cette boutique.</T></p>
                    )}
                    <div className="flex justify-between">
                      <span><T>Taxes</T></span>
                      <span>{money(quote.tax_cents)}</span>
                    </div>
                    {quote.tax_cents === 0 && (
                      <p className="text-xs text-muted-foreground"><T>Aucune taxe n'est ajoutée aux prix affichés.</T></p>
                    )}
                    <hr />
                    <div className="flex justify-between font-bold text-lg">
                      <span><T>Total</T></span>
                      <span>{money(quote.total_cents)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground"><T>Montant exact qui sera débité.</T></p>
                  </div>
                ) : null}
                {quoteError && !quoteLoading && (
                  <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {quoteError}
                  </div>
                )}
                {amountNotice && (
                  <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {amountNotice}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <T>Méthode de paiement</T>
              </CardTitle>
              <CardDescription>
                <T>Paiement 100% sécurisé</T>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <Button
                  type="button"
                  variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                  className="w-full justify-start"
                  onClick={() => setPaymentMethod('stripe')}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Carte bancaire (Stripe)
                  <Badge variant="secondary" className="ml-auto">
                    <T>Recommandé</T>
                  </Badge>
                </Button>
                
                <Button
                  type="button"
                  variant={paymentMethod === 'paypal' ? 'default' : 'outline'}
                  className="w-full justify-start"
                  onClick={() => setPaymentMethod('paypal')}
                  disabled
                >
                  PayPal
                  <Badge variant="outline" className="ml-auto">
                    <T>Bientôt</T>
                  </Badge>
                </Button>
              </div>

              <Button 
                onClick={handleSubmit}
                className="w-full" 
                size="lg"
                disabled={isProcessing || quoteLoading || !quote}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <T>Traitement...</T>
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Finaliser le paiement{quote ? ` - ${money(quote.total_cents)}` : ""}
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-3">
                En finalisant votre commande, vous acceptez nos{' '}
                <a 
                  href="/legal/terms" 
                  target="_blank" 
                  className="underline hover:text-foreground"
                >
                  conditions générales de vente
                </a>
                {' '}et notre{' '}
                <a 
                  href="/legal/privacy" 
                  target="_blank" 
                  className="underline hover:text-foreground"
                >
                  politique de confidentialité
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};